import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'https://api-invoice.ainixit.com/api';

const initialState = {
  invoices: [],
  currentInvoice: null,
  usage: null,
  isLoading: false,
  error: null,
};

// Get auth config
const getAuthConfig = (getState) => {
  const { token } = getState().auth;
  return {
    headers: { Authorization: `Bearer ${token}` },
  };
};

export const getInvoiceUsage = createAsyncThunk(
  'invoice/getInvoiceUsage',
  async (_, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_URL}/invoices/usage`, getAuthConfig(getState));
      return response.data.usage;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch usage');
    }
  }
);

// Get all invoices
export const getInvoices = createAsyncThunk(
  'invoice/getInvoices',
  async (_, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_URL}/invoices`, getAuthConfig(getState));
      return response.data.invoices;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch invoices');
    }
  }
);

// Get single invoice
export const getInvoice = createAsyncThunk(
  'invoice/getInvoice',
  async (id, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_URL}/invoices/${id}`, getAuthConfig(getState));
      return response.data.invoice;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch invoice');
    }
  }
);

// Create invoice
export const createInvoice = createAsyncThunk(
  'invoice/createInvoice',
  async (invoiceData, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(`${API_URL}/invoices`, invoiceData, getAuthConfig(getState));
      return response.data.invoice;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create invoice');
    }
  }
);

// Update invoice
export const updateInvoice = createAsyncThunk(
  'invoice/updateInvoice',
  async ({ id, data }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.put(`${API_URL}/invoices/${id}`, data, getAuthConfig(getState));
      return response.data.invoice;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update invoice');
    }
  }
);

// Delete invoice
export const deleteInvoice = createAsyncThunk(
  'invoice/deleteInvoice',
  async (id, { rejectWithValue, getState }) => {
    try {
      await axios.delete(`${API_URL}/invoices/${id}`, getAuthConfig(getState));
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete invoice');
    }
  }
);

const invoiceSlice = createSlice({
  name: 'invoice',
  initialState,
  reducers: {
    clearCurrentInvoice: (state) => {
      state.currentInvoice = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all invoices
      .addCase(getInvoices.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getInvoices.fulfilled, (state, action) => {
        state.isLoading = false;
        state.invoices = action.payload;
      })
      .addCase(getInvoices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get single invoice
      .addCase(getInvoice.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getInvoice.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentInvoice = action.payload;
      })
      .addCase(getInvoice.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create invoice
      .addCase(createInvoice.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getInvoiceUsage.fulfilled, (state, action) => {
        state.usage = action.payload;
      })
      .addCase(createInvoice.fulfilled, (state, action) => {
        state.isLoading = false;
        state.invoices.unshift(action.payload);
      })
      .addCase(createInvoice.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update invoice
      .addCase(updateInvoice.fulfilled, (state, action) => {
        const index = state.invoices.findIndex((inv) => inv._id === action.payload._id);
        if (index !== -1) {
          state.invoices[index] = action.payload;
        }
        state.currentInvoice = action.payload;
      })
      // Delete invoice
      .addCase(deleteInvoice.fulfilled, (state, action) => {
        state.invoices = state.invoices.filter((inv) => inv._id !== action.payload);
      });
  },
});

export const { clearCurrentInvoice, clearError } = invoiceSlice.actions;
export default invoiceSlice.reducer;

